import { useEffect, useState, type JSX } from "react";
import Camera, { FACING_MODES, IMAGE_TYPES } from "react-html5-camera-photo";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import { useTranslation } from "react-i18next";
import "react-html5-camera-photo/build/css/index.css";
import { isIPad13 } from "react-device-detect";
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { Box } from "@mui/material";

export type CameraDialogInitValues = void;

export type CameraDialogResultValues = {
    base64imageData?: string;
};

export type CameraDialogResult = DialogResult<CameraDialogResultValues>;

export type CameraDialogProps = DialogProps<CameraDialogInitValues, CameraDialogResult>;

export function CameraDialog({
    open,
    onClose,
    // values
}: CameraDialogProps): JSX.Element {
    const [isPreview, setIsPreview] = useState<boolean>(false);
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
    const { t } = useTranslation();

    function handleTakePhoto(dataUri: string) {
        setImageUrl(dataUri);
        setIsPreview(true);
    }

    const onOk = () => {
        onClose({
            reason: "ok",
            values: { base64imageData: imageUrl?.replace("data:image/jpeg;base64,", "") }
        });
    };

    const onCancel = () => {
        // If we're in preview mode, then cancel means retry
        if (isPreview) {
            setIsPreview(false);
            setImageUrl(undefined);
        } else {
            onClose({
                reason: "cancel",
                values: undefined
            });
        }
    };

    useEffect(() => {
        setImageUrl(undefined);
        setIsPreview(false);
    }, [open]);

    return (
        <>
            <ResponsiveDialog
                title={t("cameraDialog.attachPhoto")}
                // ipad workaround, fix for video container rendering
                //out of bounds, causing user to scroll to get to button 
                contentsx={ isIPad13 ? {
                    display: 'flex'
                } : undefined}
                enableOkay={!!isPreview}
                okayLabel={t("buttons.attach")}
                cancelLabel={isPreview ? t("buttons.retry") : t("buttons.cancel")}
                open={open}
                onClose={
                    (reason: string) => {
                        if (reason === "ok") {
                            onOk();
                        } else {
                            onCancel();
                        }
                    }
                }>

                {!isPreview && open &&
                    <Camera
                        onTakePhoto={handleTakePhoto}
                        idealFacingMode={FACING_MODES.ENVIRONMENT}
                        idealResolution={{ width: 640, height: 480 }}
                        imageType={IMAGE_TYPES.JPG}
                        imageCompression={0.97}
                        isMaxResolution={true}
                        isImageMirror={false}
                        isSilentMode={false}
                        isDisplayStartCameraError={true}
                        isFullscreen={false}
                        sizeFactor={1}
                    />
                }

                {isPreview &&
                    <Box sx={{ height: 1, width: 1 }}>
                        {imageUrl &&
                            <img src={imageUrl}
                                alt={t("cameraDialog.attachmentPreview") as string}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain"
                                }} />
                        }
                    </Box>
                }

            </ResponsiveDialog>
        </>
    );
}

export type CameraDialogState = DialogState<CameraDialogInitValues, CameraDialogResult>;
export type BindCameraDialogReturn = BindDialogReturn<CameraDialogInitValues, CameraDialogResult>;

export function useCameraDialogState(): CameraDialogState {
    return useDialogState();
}

export function bindCameraDialog(props: CameraDialogState): BindCameraDialogReturn {
    return bindDialog(props);
}
